import React, { useContext, useEffect, useState } from "react";
import _ from "lodash/fp";
import { Alert, GridGenerator } from "@actinc/dls";
import { Box, Button, Grid, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useClient } from "urql";
import { AlertContext } from "@actinc/dls/context";

import {
  AlertDialog,
  Backdrop,
  ControlledFormInput,
  ControlledFormSelect,
  ControlledFormSwitch,
  DataGrid,
  SimpleTable,
} from "../../../components";

import { relationshipTypeOptions } from "../../Workspace/MetadataCard/constants";

import { downloadCsv, validatePassageName } from "../../../services/utils";
import { setState } from "../../../services/sessionState";
import { passageNameExistsQuery } from "../../BulkProcesses/CreateContent";

const CHUNKSIZE = 15;
const CHUNKSIZE_SM = 5;

const CloneDialog = ({
  contentArea,
  fieldOptions: {
    contentSpecialistOptions,
    developmentState: developmentStateOptions,
    itemWriterOptions,
    language: languageOptions,
    elementStatus: elementStatusOptions,
    release: releaseOptions,
    restriction: restrictionOptions,
    unavailability: unavailabilityOptions,
  },
  selectedRows = [],
  itemSet,
  loading,
  mutation,
  objectType,
  onClose,
  program,
  programs,
}) => {
  const alertContext = useContext(AlertContext);
  const client = useClient();
  const { control, formState, handleSubmit, reset, setValue, watch } = useForm({
    mode: "all",
    reValidateMode: "onBlur",
    defaultValues,
  });

  const { isDirty, isSubmitting, isValid } = formState;

  const [nameFound, setNameFound] = useState({
    found: false,
    name: "",
  });
  const [result, setResult] = useState([]);
  const [resultLoading, setResultLoading] = useState(false);
  const [rowSelectionModel, setSelectionModel] = useState();
  const [itemsFailedToClone, setItemsFailedToClone] = useState([]);
  const ttsGenerate = watch("ttsGenerate");
  const isEnglish = objectType === "passage" && contentArea === "English";

  useEffect(() => {
    setSelectionModel(itemSet?.map((i) => i.id));
  }, [itemSet]);

  useEffect(() => {
    if (ttsGenerate) {
      if (isEnglish) {
        setValue("convertIPR", true);
      }
      setValue("cloneRelationships", true);
      setValue("relationshipTypeToClone", "PARENT_ACCESSIBLE");
    } else if (ttsGenerate === false) {
      if (isEnglish) {
        setValue("convertIPR", false);
      }
      setValue("cloneRelationships", false);
      setValue("relationshipTypeToClone", "");
    }
  }, [ttsGenerate, setValue, isEnglish]);

  const handlePassageNameVerification = async (passageName) => {
    if (!passageName || nameFound.name === passageName) return;
    await client
      .query(passageNameExistsQuery, {
        names: [passageName],
      })
      .toPromise()
      .then((res) => {
        const {
          data: { doPassageNamesExist },
        } = res;
        setNameFound(doPassageNamesExist[0]);
        if (doPassageNamesExist[0].found) {
          alertContext.actions.addAlert({
            message: `Passage Name ${doPassageNamesExist[0].name} already exists`,
            options: { persist: true, variant: "error" },
          });
        }
      });
  };

  const passageHeaders = [
    {
      title: "Original Passage Name",
      identifier: "originalPassageName",
    },

    {
      title: "Cloned Passage Name",
      identifier: "clonedPassageName",
    },
  ];

  const itemHeaders = [
    {
      title: `Original Item Name`,
      identifier: `originalItemName`,
    },
    {
      title: `Cloned Item Name`,
      identifier: `clonedItemName`,
    },
  ];

  const columns =
    objectType === "passage"
      ? [...passageHeaders, ...itemHeaders]
      : [...itemHeaders, { title: `Program`, identifier: `programName` }];

  const tableProps = {
    columns: columns,
    error: false,
    rows: result,
  };

  const dataGridProps = {
    checkboxSelection: true,
    columns: [
      {
        field: "name",
        flex: 1,
        headerName: "Item Name",
        sortComparator: (v1, v2) =>
          v1.localeCompare(v2, "en", { sensitivity: "base" }),
        type: "String",
      },
      {
        field: "description",
        flex: 2,
        headerName: "Description",
        type: "String",
      },
    ],
    hideFooter: true,
    loading,
    onRowSelectionModelChange: (ids) => {
      setSelectionModel(ids);
    },
    rows: itemSet,
    rowSelectionModel: rowSelectionModel,
    sortModel: [
      {
        field: "value",
        sort: "asc",
      },
    ],
    sx: { height: 250, marginBottom: 5 },
  };

  const populatedFormDataFields = (formData) => (itemsToClone) => {
    const {
      contentSpecialist,
      cloneRelationships,
      convertIPR,
      developmentState,
      elementStatus,
      language,
      newPassageName,
      relationshipTypeToClone,
      release,
      restriction,
      ttsGenerate,
      itemWriter,
      unavailability,
    } = formData;
    return {
      cloneRelationships,
      convertIPR,
      data: {
        developmentState,
        elementStatus,
        itemWriter: itemWriter?.id
          ? {
              id: itemWriter.id,
              name: itemWriterOptions.find((iw) => iw.value === itemWriter.id)
                ?.label,
            }
          : null,
        ...(language && { language }),
        program,
        release,
        restriction,
        unavailability,
        ...(contentSpecialist && {
          contentSpecialist: {
            ...contentSpecialist,
            name: contentSpecialistOptions.find(
              (cs) => cs.value === contentSpecialist.id,
            )?.label,
          },
        }),
      },
      ttsGenerate,
      itemsToClone,
      ...(newPassageName && {
        newPassageName,
      }),
      ...(objectType === "passage" && {
        passageToClone: selectedRows[0]?.id,
      }),
      ...(relationshipTypeToClone && {
        relationshipTypeToClone,
      }),
    };
  };

  const onSubmit = async (formData) => {
    setResultLoading(true);
    setItemsFailedToClone([]);

    let clonePromises = [];
    if (objectType === "passage") {
      const orderedItemIds = itemSet?.map((item) => item.id);
      const orderedCloneItemIds = rowSelectionModel
        ?.slice()
        .sort((a, b) => orderedItemIds.indexOf(a) - orderedItemIds.indexOf(b));
      clonePromises.push(doClone(formData)(orderedCloneItemIds));
    } else {
      const chunksize = formData.ttsGenerate ? CHUNKSIZE_SM : CHUNKSIZE;
      const itemToCloneChunks = _.chunk(chunksize)(
        selectedRows.map((i) => i?.id),
      );
      clonePromises = itemToCloneChunks.map(doClone(formData));
    }

    const responses = await Promise.allSettled(clonePromises);
    const clonedResults = responses
      .filter(({ status }) => status === "fulfilled")
      .map(({ value }) => value);

    setResult(
      objectType === "item"
        ? clonedResults
            .flat()
            .sort(
              (a, b) =>
                a.programName.localeCompare(b.programName) ||
                a.originalItemName.localeCompare(b.originalItemName),
            )
        : clonedResults[0] || [],
    );

    reset({ ...defaultValues });
    setResultLoading(false);
  };

  const doClone =
    (formData) =>
    async (itemIDs = []) => {
      return await client
        .mutation(mutation, {
          ...populatedFormDataFields(formData)(itemIDs),
        })
        .toPromise()
        .then((res) => {
          let convertedResponse = [];

          if (res.error) {
            convertedResponse = res.operation.variables.itemsToClone.map(
              (id) => {
                const originalItem = selectedRows.find(
                  (item) => item.id === id,
                );
                return {
                  id,
                  originalItemName: originalItem.name,
                  clonedItemName: "FAILED",
                  programName: programs.find(
                    (program) => program.id === originalItem.program,
                  ).name,
                };
              },
            );

            return convertedResponse;
          }

          if (objectType === "item") {
            convertedResponse = res?.data?.cloneItems.map((item) => {
              const [programName, originalItemName, sourceItemId] =
                item.guid.split(":");

              return {
                clonedItemName: item.name,
                id: item.id,
                originalItemName,
                programName,
                sourceItemId,
              };
            });
          } else if (objectType === "passage") {
            const { items, passage } = res?.data?.cloneItemSet;
            const listOfSelectedItemNames = itemSet
              .filter((item) => rowSelectionModel.includes(item?.id))
              .map((itemObj) => itemObj?.name);
            convertedResponse = items.map((item, idx) => ({
              id: item.name,
              originalItemName: listOfSelectedItemNames[idx],
              clonedItemName: item?.name,
            }));
            convertedResponse = [
              {
                id: passage.name,
                originalPassageName: selectedRows[0].name,
                clonedPassageName: passage.name,
              },
              ...convertedResponse,
            ];
          }
          return convertedResponse;
        });
    };

  const handleCancel = () => {
    onClose(null);
  };

  const cloneItemDialogProps = {
    open: true,
    dialogTitle: `Clone ${objectType === "item" ? "Item" : "Item Set"}`,
    customFooter: (
      <>
        <Button onClick={handleCancel} variant="text">
          {result?.length ? <>Okay</> : <>Cancel</>}
        </Button>
        {!result?.length && (
          <Button
            aria-label="Clone"
            color="primary"
            disabled={
              !isValid ||
              isSubmitting ||
              resultLoading ||
              !isDirty ||
              !!nameFound?.found
            }
            form="cloneDialogForm"
            name="Clone"
            type="submit"
          >
            Clone
          </Button>
        )}
      </>
    ),
  };

  const handleCsvDownload = () => {
    if (!result?.length) return;
    const fields = columns.map((column) => column.title);
    const data = result.map((row) => {
      return columns.map((column) => row[column.identifier] || "");
    });
    let filename;
    if (objectType === "item") filename = `clonedItems`;
    if (objectType === "passage") filename = `clonedItemSets`;
    downloadCsv({ data, fields })(filename);
  };

  return (
    <AlertDialog {...cloneItemDialogProps}>
      <Backdrop loading={resultLoading} />
      <Grid sx={{ alignItems: "center" }} container>
        <Box sx={{ flexGrow: 1 }}>
          {!!result?.length ? (
            <>
              <SimpleTable {...tableProps}></SimpleTable>
              <Box
                sx={{
                  marginTop: "1em",
                  float: "right",
                }}
              >
                <Button
                  aria-label="Download CSV"
                  color="primary"
                  onClick={handleCsvDownload}
                >
                  Download CSV
                </Button>
                {objectType === "item" && (
                  <Button
                    aria-label="View in Search"
                    color="primary"
                    onClick={() => {
                      handleCancel();
                      const itemList = result
                        .map((r) => {
                          return [r.clonedItemName, r.originalItemName];
                        })
                        .flat();
                      setState("itemSearchFilters")({ itemList });
                      window.location.reload();
                    }}
                  >
                    [View in Search]
                  </Button>
                )}
              </Box>
            </>
          ) : (
            <form id="cloneDialogForm" onSubmit={handleSubmit(onSubmit)}>
              <Box>
                {objectType === "passage" && (
                  <>
                    <Box
                      sx={{
                        maxWidth: "75%",
                      }}
                    >
                      <ControlledFormInput
                        control={control}
                        fullWidth={true}
                        id="clone-item-set-passage-name"
                        label="Passage Name"
                        name="newPassageName"
                        onBlur={(e) =>
                          handlePassageNameVerification(e.target.value)
                        }
                        rules={{
                          required: true,
                          validate: validatePassageName,
                        }}
                      />
                    </Box>
                    <Box>
                      <DataGrid {...dataGridProps} />
                    </Box>
                  </>
                )}
                {
                  <GridGenerator
                    groups={[
                      [
                        {
                          children: (
                            <ControlledFormSelect
                              control={control}
                              id="clone-item-set-content-specialist"
                              label="Content Specialist"
                              name="contentSpecialist.id"
                              options={contentSpecialistOptions}
                              rules={{
                                required: true,
                              }}
                            />
                          ),
                          key: "clone-item-set-content-specialist",
                        },
                        {
                          children: (
                            <ControlledFormSelect
                              control={control}
                              id="clone-item-set-development-state"
                              label="Development State"
                              name="developmentState"
                              options={developmentStateOptions}
                              rules={{
                                required: true,
                              }}
                            />
                          ),
                          key: "clone-item-set-development-state",
                        },
                      ],
                      [
                        {
                          children: (
                            <ControlledFormSelect
                              control={control}
                              id="clone-item-set-item-writer"
                              label="Item Writer"
                              name="itemWriter.id"
                              options={itemWriterOptions}
                            />
                          ),
                          key: "clone-item-set-item-writer",
                        },
                        {
                          children: (
                            <ControlledFormSelect
                              control={control}
                              id="clone-item-set-language"
                              label="Language"
                              name="language"
                              options={languageOptions}
                            />
                          ),
                          key: "clone-item-set-language",
                        },
                        {
                          children: (
                            <ControlledFormSelect
                              control={control}
                              id="clone-item-set-element-status"
                              label="Element Status"
                              name="elementStatus"
                              options={elementStatusOptions}
                            />
                          ),
                          key: "clone-item-set-element-status",
                        },
                      ],
                      [
                        {
                          children: (
                            <ControlledFormSelect
                              control={control}
                              id="clone-item-set-unavailability"
                              label="Unavailability"
                              name="unavailability"
                              options={unavailabilityOptions}
                            />
                          ),
                          key: "clone-item-set-unavailability",
                        },
                        {
                          children: (
                            <ControlledFormSelect
                              control={control}
                              id="clone-item-set-release"
                              label="Release"
                              name="release"
                              options={releaseOptions}
                            />
                          ),
                          key: "clone-item-set-release",
                        },
                        {
                          children: (
                            <ControlledFormSelect
                              control={control}
                              id="clone-item-set-relationship-type-to-clone"
                              label="Relationship Type"
                              name="relationshipTypeToClone"
                              options={relationshipTypeOptions.map(
                                (option) => ({
                                  label: option,
                                  value: option
                                    .replace(/-|\s/g, "_")
                                    .toUpperCase(),
                                }),
                              )}
                            />
                          ),
                          key: "clone-item-set-relationship-type-to-clone",
                        },
                        {
                          children: (
                            <ControlledFormSelect
                              control={control}
                              id="clone-item-set-restriction"
                              label="Restriction"
                              name="restriction"
                              options={restrictionOptions}
                            />
                          ),
                          key: "clone-item-set-restriction",
                        },
                        {
                          children: (
                            <ControlledFormSwitch
                              control={control}
                              id="clone-item-set-clone-relationships"
                              label="Clone Existing Relationships"
                              name="cloneRelationships"
                            />
                          ),
                          key: "clone-item-set-clone-relationships",
                        },
                        {
                          children: (
                            <ControlledFormSwitch
                              control={control}
                              id="tts-generate"
                              label="Automatically Apply TTS"
                              name="ttsGenerate"
                            />
                          ),
                          key: "tts-generate",
                        },
                        ...(isEnglish
                          ? [
                              {
                                children: (
                                  <ControlledFormSwitch
                                    control={control}
                                    id="convert-ipr"
                                    label="Convert IPR"
                                    name="convertIPR"
                                  />
                                ),
                                key: "convert-ipr",
                              },
                            ]
                          : []),
                      ],
                    ]}
                  />
                }

                <Typography variant="subtitle1">
                  NOTE: The selected value will be displayed as the Relationship
                  Type when viewing the original {objectType}:
                </Typography>
              </Box>
            </form>
          )}
          {result?.error && (
            <Alert severity={"error"}>{`Error cloning ${objectType}${
              selectedRows.length > 1 ? "s" : ""
            }`}</Alert>
          )}
        </Box>
      </Grid>
    </AlertDialog>
  );
};

const defaultValues = {
  cloneRelationships: false,
  contentSpecialist: {
    id: "",
  },
  convertIPR: false,
  developmentState: "",
  elementStatus: "",
  itemWriter: {
    id: "",
  },
  language: "",
  newPassageName: "",
  release: [],
  relationshipTypeToClone: "",
  restriction: [],
  ttsGenerate: false,
  unavailability: "",
};

export { CloneDialog };
